// c05 Forum Premium — corporate conference preview card.
//
// The catalog card uses the "outer frame + inner card" structure styled
// as a premium summit credential: a navy→champagne metallic gradient
// frame surrounds the deep-navy inner card, with hairline edges +
// bracketed CORP/SUMMIT badge at the top, and a champagne credential
// medallion at centre carrying the "TECH SUMMIT 2026" wordmark and the
// three day-thumbnails. Names slot used for the wordmark; caption for
// the date range + venue.
import { Photo, type LayoutCtx } from "./_shared";

const CHAMPAGNE_FOIL =
  "linear-gradient(135deg, #2A3A52 0%, #C9AE7F 22%, #F0DCB2 45%, #C9AE7F 65%, #2A3A52 100%)";

export function ConfTracksLayout({
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photos,
}: LayoutCtx) {
  const [ddRaw, mmRaw, yyyyRaw] = (date || "10.10.2026").split(".");
  const startDay = parseInt(ddRaw || "10", 10) || 10;
  const endDay = startDay + 2;
  const monthIdx = Math.max(1, Math.min(12, parseInt(mmRaw || "10", 10) || 10));
  const monthsAbbr = [
    "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
    "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
  ];
  const monthLabel = monthsAbbr[monthIdx - 1];
  const yearLabel = yyyyRaw || "2026";
  const dateRange = `${String(startDay).padStart(2, "0")}—${String(endDay).padStart(2, "0")} ${monthLabel} ${yearLabel}`;

  const days = [photos?.[0], photos?.[1], photos?.[2]];

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: CHAMPAGNE_FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
          fontFamily: font,
        }}
      >
        {/* Eyebrow with credential badge */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            fontFeatureSettings: '"tnum" 1, "lnum" 1',
          }}
        >
          <span style={{ opacity: 0.6 }}>[ {dateRange} ]</span>
          <span style={{ opacity: 0.6 }}>C05 / CORP</span>
        </div>

        {/* Hairline */}
        <div style={{ height: 1, width: "100%", background: `${accent}55`, marginTop: 6 }} />

        {/* Eyebrow centred */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.42em",
            color: accent,
            marginTop: "5%",
            marginBottom: "5%",
          }}
        >
          TADBIR · KORPORATIV
        </div>

        {/* Credential medallion — wordmark in a champagne ring */}
        <div
          style={{
            width: "78%",
            aspectRatio: "1 / 0.62",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 4,
              background: CHAMPAGNE_FOIL,
              padding: "4%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                background: bg,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                boxShadow: `inset 0 0 0 1px ${accent}88`,
              }}
            >
              {/* Wordmark */}
              <div
                style={{
                  fontFamily: font,
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: "0.15em",
                  color: accent,
                  lineHeight: 1,
                }}
              >
                TECH SUMMIT
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                <span style={{ width: 14, height: 1, background: accent, opacity: 0.8 }} />
                <span
                  style={{
                    fontFamily: font,
                    fontSize: 14,
                    fontWeight: 500,
                    letterSpacing: "0.2em",
                    color: ink,
                    fontFeatureSettings: '"tnum" 1, "lnum" 1',
                  }}
                >
                  {yearLabel}
                </span>
                <span style={{ width: 14, height: 1, background: accent, opacity: 0.8 }} />
              </div>
            </div>
          </div>
        </div>

        {/* Day thumbnails */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 5, width: "82%" }}>
          {days.map((src, i) => (
            <div
              key={i}
              style={{
                position: "relative",
                aspectRatio: "1 / 1",
                background: `${accent}11`,
                overflow: "hidden",
                boxShadow: `inset 0 0 0 1px ${accent}55`,
              }}
            >
              <Photo
                src={src}
                style={{ position: "absolute", inset: 0 }}
                fallback={
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: `linear-gradient(135deg, ${accent}33, ${accent}08)`,
                    }}
                  />
                }
              />
              <div
                style={{
                  position: "absolute",
                  left: 3,
                  bottom: 3,
                  padding: "2px 4px",
                  background: bg,
                  color: accent,
                  fontFamily: "var(--font-ui)",
                  fontSize: 5.5,
                  fontWeight: 700,
                  letterSpacing: "0.15em",
                  fontFeatureSettings: '"tnum" 1, "lnum" 1',
                }}
              >
                DAY {String(i + 1).padStart(2, "0")}
              </div>
            </div>
          ))}
        </div>

        <div style={{ flex: 1 }} />

        {/* Caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TASHKENT · 3 KUN · 24 MA'RUZACHI
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}aa`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
